"use client";

import { useEffect, useState, useTransition } from "react";
import Link from "@/components/LocalizedLink";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { ArrowRight, Lock, Mail, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { SofiaConnectLogo } from "@/components/SofiaConnectLogo";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";
import { useTranslation } from "@/lib/i18n";
import { makeTr } from "@/lib/tools-i18n";
import { PORTAL_LOGIN_BG } from "@/lib/tools-i18n/pages";

type Mode = "password" | "magic";

export function LoginClient({
  searchParamsPromise,
}: {
  searchParamsPromise?: Promise<{ next?: string; error?: string; info?: string }>;
}) {
  const router = useRouter();
  const { language } = useTranslation();
  const tr = makeTr(language, PORTAL_LOGIN_BG);
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [nextPath, setNextPath] = useState<string>("/portal");

  // Read query params from the server-delivered promise
  useEffect(() => {
    if (!searchParamsPromise) return;
    searchParamsPromise.then((sp) => {
      if (sp?.next) setNextPath(sp.next);
      if (sp?.error) setError(decodeURIComponent(sp.error));
      if (sp?.info) setInfo(decodeURIComponent(sp.info));
    });
  }, [searchParamsPromise]);

  async function onPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    startTransition(async () => {
      let supabase;
      try {
        supabase = createSupabaseBrowserClient();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Supabase not configured");
        return;
      }
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setError(error.message);
        return;
      }
      router.push(nextPath || "/portal");
      router.refresh();
    });
  }

  async function onMagicSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    startTransition(async () => {
      let supabase;
      try {
        supabase = createSupabaseBrowserClient();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Supabase not configured");
        return;
      }
      const redirectTo = `${window.location.origin}/api/auth/callback?next=${encodeURIComponent(nextPath || "/portal")}`;
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo },
      });
      if (error) {
        setError(error.message);
        return;
      }
      setInfo(tr("Check your inbox — we sent you a sign-in link."));
    });
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(74,158,245,0.08) 0%, transparent 60%), var(--background)",
        paddingTop: "84px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <SofiaConnectLogo className="w-10 h-10 text-[#4A9EF5]" aria-hidden />
          <div>
            <div className="text-lg font-semibold text-text-primary">Sofia Connect</div>
            <div className="text-[10px] font-mono tracking-widest uppercase text-text-muted">
              {tr("Client Portal")}
            </div>
          </div>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-8"
          style={{
            background: "var(--card-bg)",
            border: "1px solid var(--divider)",
            backdropFilter: "blur(16px)",
          }}
        >
          <h1 className="text-xl font-semibold text-text-primary mb-1">{tr("Sign in")}</h1>
          <p className="text-sm text-text-muted mb-6">
            {tr("Access your network services, billing and support.")}
          </p>

          {/* Mode toggle */}
          <div
            className="flex gap-1 p-1 rounded-xl mb-6"
            style={{ background: "var(--hover-bg)", border: "1px solid var(--divider)" }}
            role="tablist"
          >
            {(["password", "magic"] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                onClick={() => {
                  setMode(m);
                  setError(null);
                  setInfo(null);
                }}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                style={{
                  background: mode === m ? "rgba(74,158,245,0.12)" : "transparent",
                  color: mode === m ? "#4A9EF5" : "var(--text-muted)",
                  border: mode === m ? "1px solid rgba(74,158,245,0.3)" : "1px solid transparent",
                }}
              >
                {m === "password" ? tr("Password") : tr("Magic link")}
              </button>
            ))}
          </div>

          {/* Alerts */}
          {error && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg mb-4 text-sm"
              style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5" }}
              role="alert"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {info && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg mb-4 text-sm"
              style={{ background: "rgba(0,212,170,0.1)", border: "1px solid rgba(0,212,170,0.3)", color: "#5eead4" }}
              role="status"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{info}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={mode === "password" ? onPasswordSubmit : onMagicSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-text-muted mb-1.5">
                {tr("Email address")}
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg text-sm text-text-primary placeholder:text-text-muted outline-none transition-all"
                  style={{
                    background: "var(--hover-bg)",
                    border: "1px solid var(--divider)",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(74,158,245,0.5)")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "var(--divider)")}
                />
              </div>
            </div>

            {mode === "password" && (
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label htmlFor="password" className="block text-xs font-medium text-text-muted">
                    {tr("Password")}
                  </label>
                  <Link
                    href="/portal/forgot-password"
                    className="text-xs text-[#4A9EF5] hover:underline"
                  >
                    {tr("Forgot?")}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg text-sm text-text-primary placeholder:text-text-muted outline-none transition-all"
                    style={{
                      background: "var(--hover-bg)",
                      border: "1px solid var(--divider)",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(74,158,245,0.5)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "var(--divider)")}
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isPending}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "#4A9EF5", color: "#000" }}
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {mode === "password" ? tr("Sign in") : tr("Send magic link")}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Footer links */}
          <div className="mt-6 pt-6 border-t text-sm text-center" style={{ borderColor: "var(--divider)" }}>
            <span className="text-text-muted">{tr("Don't have an account?")} </span>
            <Link href="/contact" className="text-[#4A9EF5] hover:underline font-medium">
              {tr("Contact sales")}
            </Link>
          </div>
        </div>

        {/* Demo link */}
        <div className="mt-6 text-center">
          <Link
            href="/portal/demo"
            className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors"
          >
            {tr("Or explore the portal in demo mode")}
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
